import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Visualizzatore a schermo intero: frecce, tastiera (←/→/Esc), swipe su touch. items = [{src, label}].
export const Lightbox = ({ items, index, onClose, onChange }) => {
    const touchX = useRef(null);
    const total = items.length;
    const go = useCallback((d) => onChange((index + d + total) % total), [index, total, onChange]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") go(1);
            if (e.key === "ArrowLeft") go(-1);
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [go, onClose]);

    const item = items[index];
    if (!item) return null;
    const navBtn = "absolute top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/25";
    // Portale sul body: dentro una Dialog (transform) il position:fixed resterebbe confinato alla scheda.
    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={item.label}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0B1F3A]/95 p-3 sm:p-6"
            onClick={onClose}
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                touchX.current = null;
                if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
            }}
            data-testid="attachment-lightbox"
        >
            <button type="button" onClick={onClose} className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25" aria-label="Chiudi" data-testid="attachment-lightbox-close">
                <X className="h-5 w-5" />
            </button>
            {total > 1 && (
                <>
                    <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} className={`${navBtn} left-2 sm:left-6`} aria-label="Immagine precedente" data-testid="attachment-lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} className={`${navBtn} right-2 sm:right-6`} aria-label="Immagine successiva" data-testid="attachment-lightbox-next"><ChevronRight className="h-6 w-6" /></button>
                </>
            )}
            <img
                key={item.src}
                src={item.src}
                alt={item.label}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[82vh] max-w-full select-none rounded-2xl object-contain shadow-2xl"
                data-testid="attachment-lightbox-image"
            />
            <div className="absolute bottom-5 left-0 right-0 px-16 text-center text-sm font-semibold text-white/85" data-testid="attachment-lightbox-caption">
                {item.label}{total > 1 && <span className="ml-2 text-white/50">{index + 1} / {total}</span>}
            </div>
        </div>,
        document.body,
    );
};
